import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import { GetPublicQuestionDetailsCall } from "../../redux/api/question.api";

const isVideoUrl = (url = "") => /\.(mp4|mov|m4v|webm)(\?|$|#)/i.test(String(url));

export default function PublicQuestionPage() {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [question, setQuestion] = useState(null);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setError("");
      const res = await GetPublicQuestionDetailsCall(id);
      if (!mounted) return;
      if (res?.status === 200 && res?.question) {
        setQuestion(res.question);
      } else {
        setError("Question not found or unavailable.");
      }
      setLoading(false);
    };
    load();
    return () => {
      mounted = false;
    };
  }, [id]);

  useEffect(() => {
    const ua = navigator.userAgent || "";
    const isMobile = /Android|iPhone|iPad|iPod/i.test(ua);
    if (!isMobile || !id) return;

    const appUrl = `microlab://question/${id}`;
    const timer = setTimeout(() => {
      window.location.href = appUrl;
    }, 200);

    return () => clearTimeout(timer);
  }, [id]);

  const media = useMemo(() => {
    const files = Array.isArray(question?.files) ? question.files : [];
    return files.filter((f) => f?.url);
  }, [question]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "radial-gradient(1200px 600px at 20% -10%, rgba(33,150,243,0.18), transparent), radial-gradient(1000px 500px at 90% -20%, rgba(156,39,176,0.18), transparent), #0b1220",
        py: { xs: 5, md: 8 },
      }}
    >
      <Container maxWidth="md">
        <Card
          sx={{
            p: { xs: 2.2, md: 3 },
            mb: 2.2,
            borderRadius: 3,
            bgcolor: "rgba(8, 15, 35, 0.62)",
            border: "1px solid rgba(255,255,255,0.2)",
            backdropFilter: "blur(10px)",
          }}
        >
          <Stack spacing={1.2}>
            <Chip
              label="Public Question"
              sx={{
                bgcolor: "rgba(255,255,255,0.12)",
                color: "#fff",
                width: "fit-content",
              }}
            />
            <Typography variant="h4" sx={{ color: "#fff", fontWeight: 800 }}>
              Microlab Community
            </Typography>
            <Typography variant="body1" sx={{ color: "rgba(255,255,255,0.92)" }}>
              Shared question details are visible publicly. Open the app for full interactive
              experience.
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} pt={0.4}>
              <Button
                component={Link}
                to="/authentication/sign-in"
                variant="contained"
                sx={{ textTransform: "none", fontWeight: 700, bgcolor: "#1e88e5" }}
              >
                Login to Dashboard
              </Button>
            </Stack>
          </Stack>
        </Card>

        <Card
          sx={{
            p: { xs: 2.2, md: 3 },
            borderRadius: 3,
            bgcolor: "rgba(255,255,255,0.07)",
            border: "1px solid rgba(255,255,255,0.16)",
            backdropFilter: "blur(8px)",
            color: "#fff",
          }}
        >
          {loading ? (
            <Stack alignItems="center" py={7}>
              <CircularProgress />
            </Stack>
          ) : error ? (
            <Typography sx={{ color: "#ffb4b4" }}>{error}</Typography>
          ) : (
            <Stack spacing={2.25}>
              <Typography variant="h5" fontWeight={800} sx={{ color: "#fff" }}>
                Community Question
              </Typography>
              <Typography variant="body1" sx={{ whiteSpace: "pre-wrap", color: "#fff" }}>
                {question?.body || "-"}
              </Typography>

              {media.length > 0 && (
                <Stack spacing={1.5}>
                  {media.map((f) =>
                    isVideoUrl(f?.url) ? (
                      <Box
                        key={f.id || f.url}
                        component="video"
                        controls
                        src={f.url}
                        sx={{ width: "100%", borderRadius: 2, background: "#000" }}
                      />
                    ) : (
                      <Box
                        key={f.id || f.url}
                        component="img"
                        src={f.url}
                        alt="question media"
                        sx={{ width: "100%", borderRadius: 2, display: "block" }}
                      />
                    )
                  )}
                </Stack>
              )}

              <Divider sx={{ borderColor: "rgba(255,255,255,0.18)" }} />
              <Typography variant="subtitle2" sx={{ color: "rgba(255,255,255,0.88)" }}>
                Comments ({question?.comments?.length || 0})
              </Typography>
              <Stack spacing={1.25}>
                {(question?.comments || []).map((c) => (
                  <Card
                    key={c.id}
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      bgcolor: "rgba(255,255,255,0.03)",
                      borderColor: "rgba(255,255,255,0.2)",
                    }}
                  >
                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.75)" }}>
                      {c?.user?.first_name || "User"} {c?.user?.last_name || ""}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#fff" }}>
                      {c?.comment}
                    </Typography>
                  </Card>
                ))}
              </Stack>
            </Stack>
          )}
        </Card>
      </Container>
    </Box>
  );
}
